'use es6';

import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import UILink from 'UIComponents/link/UILink';
import MediaQuery from 'marketplace-ui-common/layout/components/MediaQuery';
import * as RequestState from 'ecosystem-marketplace-core/constants/RequestState';
import { isSucceeded } from 'ecosystem-marketplace-core/utils/requestState';
import { DEFAULT_SEARCH_LIMIT, FILTER_TAG_KEYS } from '../../constants/search/Filters';
import * as FilterKeys from '../../constants/search/FilterKey';
import { getCredentialDisplayName, getStorefrontResultsHeaderText } from '../../utils/i18n';
import { getStorefrontProfilesTotal } from '../../selectors/storefront';
import { getAccreditations, getAccreditationsRequestState } from '../../selectors/accreditations';
import SortDropdown from './SortDropdown';
import ResultsFilters from './ResultsFilters';
import FilterPanelNavigator from '../filters/mobile/FilterPanelNavigator';
import StorefrontResultsHeader from 'ecosystem-marketplace-core/components/storefront/StorefrontResultsHeader';
import * as ScreenSizes from 'ecosystem-marketplace-core/constants/layout/ScreenSizes';
import getLang from 'I18n/utils/getLang';
import { getCredentialById } from '../../utils/credentials';
import { getServices } from '../../slices/serviceCatalogSlice';
import FindPartnerWizard from '../homepage/questionnaire/FindPartnerWizard';
import { clearRecentActivity } from '../../utils/homepage';
const resultsHeaderTextPath = 'ecosystem-marketplace-solutions-ui.storefront.results.header';
const resultsCountTextPath = 'ecosystem-marketplace-solutions-ui.storefront.results.resultsCount';

const ResultsHeader = ({
  filters,
  onClearFilters,
  onDeleteFilter,
  onUpdateFilter,
  requestState,
  hasResults
}) => {
  const lang = getLang();
  const accreditations = useSelector(getAccreditations);
  const accreditationRequestState = useSelector(getAccreditationsRequestState);
  const catalogService = useSelector(getServices);
  const total = useSelector(getStorefrontProfilesTotal);
  const filtersCount = useMemo(() => Object.keys(filters).filter(key => FILTER_TAG_KEYS.indexOf(key) !== -1 && filters[key] && filters[key].length >= 1).length, [filters]);
  const headerText = useMemo(() => {
    let text = getStorefrontResultsHeaderText(filters, catalogService);

    if (filters[FilterKeys.ACCREDITATION].length === 1 && filtersCount === 1) {
      const accreditationDisplayName = getCredentialDisplayName(getCredentialById(accreditations, filters[FilterKeys.ACCREDITATION]), lang);
      text = /*#__PURE__*/_jsx(FormattedMessage, {
        message: `${resultsHeaderTextPath}.accreditation`,
        options: {
          accreditation: accreditationDisplayName.replace('Accreditation', '').trim()
        }
      });
    }

    return isSucceeded(accreditationRequestState) && text;
  }, [accreditationRequestState, accreditations, catalogService, filters, filtersCount, lang]);
  const location = useLocation();
  const [showFindPartnerWizard, setShowFindPartnerWizard] = useState(false);
  const redirectedFromHomepage = location.search.includes('ref=questionnaire');
  const renderedResultsCount = useMemo(() => {
    if (!isSucceeded(requestState) || !total) {
      return null;
    }

    const searchTerm = filters[FilterKeys.SEARCH];
    const options = {
      start: filters[FilterKeys.OFFSET] + 1,
      end: Math.min(total, filters[FilterKeys.OFFSET] + DEFAULT_SEARCH_LIMIT),
      total
    };

    if (filters[FilterKeys.SEARCH]) {
      return /*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: `${resultsCountTextPath}.searchTerm`,
        options: Object.assign({}, options, {
          searchTerm
        })
      });
    }

    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: `${resultsCountTextPath}.default`,
      options: options
    });
  }, [filters, requestState, total]);
  const renderedTitle = useMemo(() => {
    if (redirectedFromHomepage) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "ecosystem-marketplace-solutions-ui.storefront.results.header.yourResults"
      });
    }

    return hasResults && headerText;
  }, [hasResults, headerText, redirectedFromHomepage]);

  const defaultHeader = /*#__PURE__*/_jsx("div", {
    children: /*#__PURE__*/_jsx(StorefrontResultsHeader, {
      title: renderedTitle,
      description: redirectedFromHomepage && /*#__PURE__*/_jsxs("span", {
        children: [/*#__PURE__*/_jsx(FormattedMessage, {
          message: "ecosystem-marketplace-solutions-ui.storefront.results.header.questionnaire"
        }), /*#__PURE__*/_jsx(UILink, {
          onClick: () => {
            clearRecentActivity();
            setShowFindPartnerWizard(true);
          },
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "ecosystem-marketplace-solutions-ui.storefront.results.header.takeQuestionnaireAgain"
          })
        })]
      }),
      renderedFiltersPanel: /*#__PURE__*/_jsx(FilterPanelNavigator, {
        filters: filters,
        location: location,
        onClearFilters: onClearFilters,
        onDeleteFilter: onDeleteFilter,
        onSetFilter: onUpdateFilter
      }),
      renderedResultsCount: renderedResultsCount,
      renderedResultsFilters: /*#__PURE__*/_jsx(ResultsFilters, {
        className: "m-bottom-4",
        filters: filters,
        onClearFilters: onClearFilters,
        onCloseFilterTag: onDeleteFilter
      }),
      renderedSortDropdown: hasResults && /*#__PURE__*/_jsx(SortDropdown, {
        filters: filters
      })
    })
  });

  return /*#__PURE__*/_jsx(MediaQuery, {
    minScreenSize: ScreenSizes.SM,
    alternate: /*#__PURE__*/_jsxs("div", {
      children: [/*#__PURE__*/_jsx(StorefrontResultsHeader, {
        ungatedMobile: true,
        title: headerText,
        renderedResultsCount: renderedResultsCount
      }), showFindPartnerWizard && /*#__PURE__*/_jsx(FindPartnerWizard, {
        onClose: () => setShowFindPartnerWizard(false),
        isMobileView: true
      })]
    }),
    children: /*#__PURE__*/_jsxs("div", {
      children: [defaultHeader, showFindPartnerWizard && /*#__PURE__*/_jsx(FindPartnerWizard, {
        onClose: () => setShowFindPartnerWizard(false),
        isMobileView: false
      })]
    })
  });
};

ResultsHeader.propTypes = {
  filters: PropTypes.object.isRequired,
  onClearFilters: PropTypes.func.isRequired,
  onDeleteFilter: PropTypes.func.isRequired,
  onUpdateFilter: PropTypes.func.isRequired,
  requestState: PropTypes.oneOf(Object.values(RequestState)).isRequired,
  hasResults: PropTypes.bool
};
export default ResultsHeader;